import React, { memo } from "react";
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Grid,
} from "@mui/material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import useStyles from "./styles";
import classNames from "classnames";
import { ErrorTriangleOutlineIcon } from "../../providers/RexIconsSVG/RexIconsSVG";

interface IInput {
  id: string;
  value: string | number | boolean | any;
  label?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  helperText?: string;
  autoComplete?: string;
  error?: boolean;
  placeholder?: string;
  margin?: "dense" | "none";
  type?: string;
  disabled?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  customBodyPosition?: string;
  copyEnabled?: boolean;
  style?: React.CSSProperties;
  manualClass?: string;
  errorMessage?: string;
  isTouched?: boolean;
  customLabelStyle?: string;
  customErrorStyle?: string;
  multiline?: boolean;
  required?: boolean;
  rows?: string | number;
  maxLength?: number;
  inputRef?: React.Ref<any>;
  labelFormatter?: (data: any) => any;
  showAdormentOnError?: boolean;
  min?: number;
}

const Input = ({
  id,
  value,
  label,
  onClick,
  onChange,
  onBlur,
  onKeyPress,
  onKeyDown,
  helperText,
  autoComplete,
  error,
  placeholder,
  margin = "none",
  type = "text",
  disabled = false,
  startAdornment,
  endAdornment,
  customBodyPosition,
  copyEnabled = false,
  style = {},
  manualClass,
  errorMessage,
  isTouched,
  customLabelStyle,
  customErrorStyle,
  multiline,
  required = false,
  rows,
  maxLength = 3000,
  inputRef,
  labelFormatter,
  min = null,
  showAdormentOnError,
}: IInput) => {
  const classes = useStyles();
  const isErrorOccurs = isTouched && errorMessage;

  const getEndAdornment = () => {
    if (copyEnabled)
      return (
        <CopyToClipboard className={classes.copyToClipboard} text={value}>
          <FileCopyIcon />
        </CopyToClipboard>
      );
    return (
      isErrorOccurs && (
        <ErrorTriangleOutlineIcon
          viewBox="0 0 16 16"
          className={classes.errorIcon}
        />
      )
    );
  };

  return (
    <FormControl className={classNames(classes.formControl, customBodyPosition)}>
      <InputLabel
        required={required}
        className={classNames(
          customLabelStyle ? customLabelStyle : null,
          isErrorOccurs ? classes.errorLabel : null,
          disabled ? classes.disableInputLabel : null,
          classes.inputLabel,
        )}
        shrink
        htmlFor={id}
      >
        {(labelFormatter && labelFormatter(label)) || label}
      </InputLabel>
      <OutlinedInput
        style={style}
        value={value}
        inputProps={{
          className: classes.inputAutofill,
          maxLength,
          min,
          "data-testid": id,
        }}
        onChange={onChange}
        onClick={onClick}
        onBlur={onBlur}
        id={id}
        classes={{
          input: !disabled
            ? classNames(classes.borderBox, manualClass)
            : classes.disabledInput,
          notchedOutline: isErrorOccurs && classes.errorInput,
        }}
        className={classNames(isErrorOccurs ? classes.selectFluidError : classes.selectFluid)}
        margin={margin}
        placeholder={placeholder}
        error={error}
        autoComplete={autoComplete}
        type={type}
        disabled={disabled}
        onKeyPress={onKeyPress}
        onKeyDown={onKeyDown}
        startAdornment={startAdornment}
        endAdornment={
          (endAdornment && !isErrorOccurs && !helperText) || showAdormentOnError
            ? endAdornment
            : getEndAdornment()
        }
        multiline={multiline}
        rows={rows}
        inputRef={inputRef}
      />
      {helperText && (
        <Grid container>
          <FormHelperText className={classes.helperText} error={error}>
            {helperText}
          </FormHelperText>
        </Grid>
      )}
      {isErrorOccurs && (
        <Grid container>
          {/* <ReportProblemIcon style={{ color: "red", width: 14 }} /> */}
          <FormHelperText
            data-testid={`errors-${id}`}
            className={classNames(
              customErrorStyle ? customErrorStyle : null,
              classes.errorSpan,
            )}
          >
            {errorMessage}
          </FormHelperText>
        </Grid>
      )}
    </FormControl>
  );
};

export default memo(Input);
